import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { LOCAL_STORAGE, WebStorageService } from "ngx-webstorage-service";
import { BehaviorSubject, forkJoin, Observable, Subject } from "rxjs";
import { GlobalVariable } from "../app.component";
import { DataService, Response } from "../dataService/data.service";
import { FeaturesByPlanService } from "../services/features-by-plan/features-by-plan.service";
import {
  MessagingStructure,
  MessagingSubFeatures,
  MESSAGING_SUBFEATURES,
} from "../services/features-by-plan/interface/messaging";
import { CurrentSection } from "./interface/messaging";
import {
  FieldValues,
  Post,
  StoreFieldValues,
} from "./interface/transactional/transactional";

@Injectable({
  providedIn: "root",
})
export class MessagingService {
  private BASE_URL = GlobalVariable;
  private _setFieldValues = new Subject<FieldValues>();
  public setFieldValues$ = this._setFieldValues.asObservable();
  private _resetFields = new Subject<{
    onReset: boolean;
    onDiscard: boolean;
  }>();
  public resetFields$ = this._resetFields.asObservable();
  private _setCurrentSection = new Subject<CurrentSection>();
  public setCurrentSection$ = this._setCurrentSection.asObservable();
  private _onChangeSection = new Subject<CurrentSection>();
  public onChangeSection$ = this._onChangeSection.asObservable();
  private _storeFieldValues = new Subject<StoreFieldValues>();
  public storeFieldValues$ = this._storeFieldValues.asObservable();
  private _editPost = new Subject<Post>();
  public editPost$ = this._editPost.asObservable();
  private _postSuccess = new Subject<boolean>();
  public postSuccess$ = this._postSuccess.asObservable();
  private _messagingCount = new BehaviorSubject<any>(null);
  public messagingCount$ = this._messagingCount.asObservable();
  private _promotionalCount = new BehaviorSubject<any>(null);
  public promotionalCount$ = this._promotionalCount.asObservable();
  private _contentChange = new BehaviorSubject<any>(null);
  public contentChange$ = this._contentChange.asObservable();
  private _dynamicVariableChange = new BehaviorSubject<any>(null);
  public dynamicVariableChange$ = this._dynamicVariableChange.asObservable();
  public closeCalendarModal$ = new BehaviorSubject<boolean>(null);

  messagingFeature: MessagingStructure = {
    zceppa_template: [
      "feature_messaging",
      MESSAGING_SUBFEATURES.zceppa_template,
      "is_applicable",
    ],
    custom_template: [
      "feature_messaging",
      MESSAGING_SUBFEATURES.custom_template,
      "is_applicable",
    ],
    transactional_channel: [
      "feature_messaging",
      MESSAGING_SUBFEATURES.transactional_channel,
      "values",
      ["Email", "SMS", "WhatsApp"],
    ],
    promotional_channel: [
      "feature_messaging",
      MESSAGING_SUBFEATURES.promotional_channel,
      "values",
      ["Email", "SMS", "WhatsApp"],
    ],
    promotional_download_report: [
      "feature_messaging",
      MESSAGING_SUBFEATURES.promotional_download_report,
      "is_applicable",
    ],
    transactional_download_report: [
      "feature_messaging",
      MESSAGING_SUBFEATURES.transactional_download_report,
      "is_applicable",
    ],
    advance_scheduling: [
      "feature_messaging",
      MESSAGING_SUBFEATURES.advance_scheduling,
      "is_applicable",
    ],
  };
  allowedChannels: string[] = [];

  constructor(
    @Inject(LOCAL_STORAGE) private storage: WebStorageService,
    private httpService: DataService,
    private featuresByPlan: FeaturesByPlanService,
    private router: Router
  ) {
    this.checkFeatureValues("transactional_channel").then(
      (response: (string | any)[]) => {
        this.allowedChannels = response || [];
      }
    );
  }

  async checkIsFeatureApplicable(subFeature: keyof MessagingSubFeatures) {
    return (
      await this.featuresByPlan.getFeature(this.messagingFeature[subFeature])
    ).isApplicable();
  }

  async checkFeatureValues(subFeature: keyof MessagingSubFeatures) {
    let subFeatureDetails: any = [...this.messagingFeature[subFeature]];

    let response = (
      await this.featuresByPlan.getFeature(subFeatureDetails)
    ).checkValues();
    return response;
  }
  setFieldValues(data: FieldValues) {
    this._setFieldValues.next(data);
  }
  resetFields(data: { onReset: boolean; onDiscard: boolean }) {
    this._resetFields.next(data);
  }
  setCurrentSection(data: CurrentSection) {
    this._setCurrentSection.next(data);
  }
  onChangeSection(data: CurrentSection): void {
    this._onChangeSection.next(data);
  }
  onStoreFieldValues(data: StoreFieldValues): void {
    this._storeFieldValues.next(data);
  }
  onEditPost(data: Post): void {
    this._editPost.next(data);
  }
  onPostSuccess(data: boolean): void {
    this._postSuccess.next(data);
  }
  onContentChange(content: string): void {
    this._contentChange.next(content);
  }

  changeRoute() {
    let route: string = `messaging/transactional/create`;
    this.router.navigate([route]);
  }
  setTotalCount(data: {
    count: number;
    type: "SENT" | "DRAFT";
    product: "TRANSACTIONAL" | "PROMOTIONAL";
  }) {
    if (data.product == "TRANSACTIONAL") {
      this._messagingCount.next(data);
    } else {
      this._promotionalCount.next(data);
    }
  }

  updateDynamicVariable(value) {
    this._dynamicVariableChange.next(value);
  }

  redirectToCustomerList(customerListId: string) {
    this.router.navigate([`/customers/lists/${customerListId}`]);
  }

  get outlet_id() {
    return this.storage.get("outlet_id");
  }

  get isAccessMenu() {
    return this.storage.get("access").main_menu;
  }

  get checkInternationalCountry() {
    return this.storage.get("business_country_name") !== "India";
  }

  checkCreditUsed(content: string) {
    content = content || "";
    for (var i = 0, n = content.length; i < n; i++) {
      if (content.charCodeAt(i) > 255) {
        return {
          usedCredits: this._usedCredits(content.length, "regional"),
          isRegional: true,
        };
      }
    }
    return {
      usedCredits: this._usedCredits(content.length, "standard"),
      isRegional: false,
    };
  }

  private _usedCredits(smslen: number, type: "regional" | "standard") {
    return Math.ceil(Number(smslen) / (type == "standard" ? 160 : 70));
  }

  public interactWhatsAppTemplateList(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.INTERACT_GET_WHATSAPP_TEMPLATE(
        this.outlet_id
      ),
    });
  }

  /**
   * defaultTemplateAPI - get default template ID
   */
  public defaultTemplateAPI(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.REVIEW_SETUP_VALUE_URL_V2(this.outlet_id),
    });
  }
  /**
   * getCustomerListDropdown - get customerList on dropdown
   */
  public getCustomerListDropdownAPI(
    customerName: string
  ): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.FILTER_CUSTOMER_URL(this.outlet_id),
      keyword: customerName,
    });
  }
  /**
   * getCustomerListDropdown - get customerList on dropdown
   */
  public getCustomerListAPI(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.CUSTOMER_LIST_API_V2(this.outlet_id),
    });
  }

  /**
   * getCustomerListDropdown - get customerList on dropdown
   */
  public getPredefiledVariablesAPI(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.GET_PREDEFINED_VARIABLES(this.outlet_id),
    });
  }

  public getMessagingMetaDataAPI(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.GET_MESSAGING_METADATA(this.outlet_id),
    });
  }

  public getTransactionalTemplate(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.MESSAGING_SETTINGS_GET_TRANSACTIONAL_TEMPLATE(
        this.outlet_id
      ),
    });
  }

  public getPromotionalTemplate(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.MESSAGING_SETTINGS_GET_PROMOTIONAL_TEMPLATE(
        this.outlet_id
      ),
    });
  }

  public getMessagingTransactionalTemplates(filters: {
    page?: string;
    size?: string;
    keyword?: string;
    channel?: string;
    template_id?: string;
  }): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.MESSAGING_GET_TRANSACTIONAL_TEMPLATES(
        this.outlet_id
      ),
      page: filters?.page,
      size: filters?.size,
      keyword: filters?.keyword,
      channel: filters?.channel,
      template_id: filters?.template_id,
    });
  }

  public getMessagingPromotionalTemplates(filters: {
    page?: string;
    size?: string;
    keyword?: string;
    channel?: string;
  }): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.MESSAGING_GET_PROMOTIONAL_TEMPLATES(
        this.outlet_id
      ),
      page: filters?.page,
      size: filters?.size,
      keyword: filters?.keyword,
      channel: filters?.channel,
    });
  }

  public createTransactionalTemplate(payload: any): Observable<Response> {
    return this.httpService.postRequest({
      customApiUrl:
        this.BASE_URL.MESSAGING_SETTINGS_CREATE_TRANSACTIONAL_TEMPLATE(
          this.outlet_id
        ),
      data: payload,
    });
  }

  public createPromotionalTemplate(payload: any): Observable<Response> {
    return this.httpService.postRequest({
      customApiUrl:
        this.BASE_URL.MESSAGING_SETTINGS_CREATE_PROMOTIONAL_TEMPLATE(
          this.outlet_id
        ),
      data: payload,
    });
  }

  public sendNewMessageByContact(
    payload: any,
    messagingType: string,
    channel: string
  ): Observable<Response> {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.MESSAGING_POST_MESSAGE_BY_CONTACT(
        this.outlet_id,
        messagingType,
        channel
      ),
      data: payload,
    });
  }

  public sendNewMessageByCustomerList(
    payload: any,
    messagingType: string,
    channel: string
  ): Observable<Response> {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.MESSAGING_POST_MESSAGE_BY_CUSTOMERLIST(
        this.outlet_id,
        messagingType,
        channel
      ),
      data: payload,
    });
  }

  public sendNewMessageByExcel(
    payload: any,
    messagingType: string,
    channel: string
  ): Observable<Response> {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.MESSAGING_POST_MESSAGE_BY_EXCEL(
        this.outlet_id,
        messagingType,
        channel
      ),
      data: payload,
    });
  }

  public updateMessageByContact(
    payload: any,
    messagingType: string,
    channel: string,
    template_id: string
  ): Observable<Response> {
    return this.httpService.putRequest({
      customApiUrl: this.BASE_URL.MESSAGING_UPDATE_MESSAGE_BY_CONTACT(
        this.outlet_id,
        messagingType,
        channel,
        template_id
      ),
      data: payload,
    });
  }

  public updateMessageByCustomerList(
    payload: any,
    messagingType: string,
    channel: string,
    template_id: string
  ): Observable<Response> {
    return this.httpService.putRequest({
      customApiUrl: this.BASE_URL.MESSAGING_UPDATE_MESSAGE_BY_CUSTOMERLIST(
        this.outlet_id,
        messagingType,
        channel,
        template_id
      ),
      data: payload,
    });
  }

  public updateMessageByExcel(
    payload: any,
    messagingType: string,
    channel: string,
    template_id: string
  ): Observable<Response> {
    return this.httpService.putRequest({
      customApiUrl: this.BASE_URL.MESSAGING_UPDATE_MESSAGE_BY_EXCEL(
        this.outlet_id,
        messagingType,
        channel,
        template_id
      ),
      data: payload,
    });
  }

  public deleteDraftMessage(messageId: string): Observable<Response> {
    return this.httpService.deleteRequest({
      customApiUrl: this.BASE_URL.MESSAGING_DELETE_DRAFT(
        this.outlet_id,
        messageId
      ),
    });
  }

  public getScheduledMessages(
    msgType: string,
    filters: {
      start: string;
      end: string;
      channel?: string;
      status?: string;
    }
  ): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.GET_SCHEDULED_MESSAGES(
        this.outlet_id,
        msgType
      ),
      start: filters.start,
      end: filters.end,
      channel: filters?.channel,
      status: filters?.status,
    });
  }

  public getMessageDetailsFromMessageId(
    messageId: string
  ): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.MESSAGING_DETAILS_FROM_MESSAGEID(
        this.outlet_id,
        messageId
      ),
    });
  }

  public getTemplateDetailsFromId(templateId: string): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.MESSAGING_TEMPLATE_DETAILS_FROM_ID(
        this.outlet_id,
        templateId
      ),
    });
  }

  public getPostCount() {
    forkJoin([
      this.getAllSentTransactionalMessage(),
      this.getAllDraftTransactionalMessage(),
    ]).subscribe((response: [Response, Response]) => {
      if (response[0].code == 200 && response[1].code == 200) {
        let sentPost = response[0].data;
        let draftPost = response[1].data;
        let totalSentCount = sentPost["total_sent_message_count"];
        let totalDraftCount = draftPost["total_draft_message_count"];
        this.setTotalCount({
          count: totalDraftCount,
          type: "DRAFT",
          product: "TRANSACTIONAL",
        });
        this.setTotalCount({
          count: totalSentCount,
          type: "SENT",
          product: "TRANSACTIONAL",
        });
      }
    });
  }

  public getAllSentTransactionalMessage(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.MESSAGING_SENT_FILTER(this.outlet_id, "all"),
    });
  }

  public getAllDraftTransactionalMessage(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.MESSAGING_DRAFT_FILTER(this.outlet_id, "all"),
    });
  }

  public updateTransactionalTemplate(
    payload: any,
    templateId: string
  ): Observable<Response> {
    return this.httpService.putRequest({
      customApiUrl:
        this.BASE_URL.MESSAGING_UPDATE_DELETE_TRANSACTIONAL_TEMPLATE(
          this.outlet_id,
          templateId
        ),
      data: payload,
    });
  }

  public updatePromotionalTemplate(
    payload: any,
    templateId: string
  ): Observable<Response> {
    return this.httpService.putRequest({
      customApiUrl: this.BASE_URL.MESSAGING_UPDATE_DELETE_PROMOTIONAL_TEMPLATE(
        this.outlet_id,
        templateId
      ),
      data: payload,
    });
  }

  /**
   * getPermissionsAPI - get permissions | sent post count
   */
  public getPermissionsAPI(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.MESSAGING_SMS_SENT_FILTER_V2(this.outlet_id),
      page: "1",
      size: "1",
    });
  }

  /**
   * getDraftPostAPI - get draft post count
   */
  public getDraftPostAPI(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.MESSAGING_SMS_DRAFT_FILTER_V2(this.outlet_id),
      page: "1",
      size: "1",
    });
  }
  /**
   * getMessagingSettings - get default channels and template
   */
  public getMessagingSettings(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.MESSAGING_COMMON_SETTINGS(this.outlet_id),
    });
  }

  /**
   * getWhatsAppTemplateMetaData - get whatsapp metadata
   */
  public getWhatsAppTemplateMetaData(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.GET_WHATSAPP_METADATA(this.outlet_id),
    });
  }

  /**
   * uploadWhatsAppMedia - uploading an media for whatsapp
   */
  public uploadWhatsAppMedia(formData): Observable<Response> {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.SOCIAL_UPLOAD(this.outlet_id),
      data: formData,
    });
  }

  /**
   * uploadWhatsAppVideo - uploading an video for whatsapp
   */
  public uploadWhatsAppVideo(formData): Observable<Response> {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.SOCIAL_VIDEO_UPLOAD(this.outlet_id),
      data: formData,
    });
  }

  /**
   * deletewhatAppTemplate - delete whatsapp template
   */
  public deletewhatAppTemplate(templateId: string): Observable<Response> {
    return this.httpService.deleteRequest({
      customApiUrl:
        this.BASE_URL.MESSAGING_UPDATE_DELETE_TRANSACTIONAL_TEMPLATE(
          this.outlet_id,
          templateId
        ),
    });
  }

  public deleteBulkWhatAppTemplate(payload): Observable<Response> {
    return this.httpService.deleteRequestWithPayload({
      customApiUrl: this.BASE_URL.MESSAGING_DELETE_BULK_TRANSACTIONAL_TEMPLATE(
        this.outlet_id
      ),
      data: payload,
    });
  }

  /**
   * deletewhatAppTemplateMedia - delete whatsapp template media attachment
   */
  public deletewhatAppTemplateMedia(
    mediaId: string
    // template_id: string
  ): Observable<Response> {
    return this.httpService.deleteRequest({
      customApiUrl: this.BASE_URL.SOCIAL_MEDIA_DELETE(this.outlet_id, mediaId),
    });
  }

  /**
   * whatsappVerifyAPI - get whatsapp available
   */
  public whatsappVerifyAPI(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.WHATSAPP_VERIFY_V3(this.outlet_id),
    });
  }
  /**
   * whatsappEnabledInteractAPI - get whatsapp allowed in interact
   */
  public whatsappEnabledInteractAPI(): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.INTERACT_SETTINGS(this.outlet_id),
    });
  }
  /**
   * updateBulkWhatsappAPI - update whatsapp message contact list
   */
  // public updateBulkWhatsappAPI(payload: {
  //   dynamic_variables?: {};
  //   status?: string;
  //   dlt_template_id?: string;
  //   sms_text?: string;
  //   title?: string;
  //   template_id?: string;
  //   customer_list_id?: any;
  // }): Observable<Response> {
  //   return this.httpService.postRequest({
  //     customApiUrl: this.BASE_URL.SEND_BULK_WHATSAPP_MESSAGE_V2(this.outlet_id),
  //     data: payload,
  //   });
  // }
  /**
   * sendWhatsappMessageAPI - send whatsapp message single
   */
  public sendWhatsappMessageAPI(payload: {
    status?: string;
    template_id?: string;
    dlt_template_id?: string;
    sms_text?: string;
    title?: string;
    dynamic_variables?: {};
    first_name?: string;
    last_name?: string;
    phone_number?: any;
    country_code?: number;
  }): Observable<Response> {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.SEND_WHATSAPP_MESSAGE_V2(this.outlet_id),
      data: payload,
    });
  }

  /**
   * verifyWhatsappOptin - verify whatsapp optin
   */
  public verifyWhatsappOptin(payload: {
    phone_number: any;
    country_code: number;
  }): Observable<Response> {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.VERIFY_OPTIN(this.outlet_id),
      data: payload,
    });
  }
  /**
   * postMessagingAPI - postMessagingAPI
   */
  public postMessagingAPI(payload: {
    status?: string;
    template_id?: string;
    dlt_template_id?: string;
    sms_text?: string;
    title?: string;
    dynamic_variables?: {};
    first_name?: string;
    last_name?: string;
    phone_number?: any;
    country_code?: number;
  }): Observable<Response> {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.MESSAGING_SMS_SENT_V2(this.outlet_id),
      data: payload,
    });
  }

  /**
   * postMessagingExcelAPI - postMessagingExcelAPI
   */
  public postMessagingExcelAPI(
    payload,
    messagingChannel,
    messagingType: "transactional" | "promotional"
  ): Observable<Response> {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.MESSAGING_BULK_EXCEL(
        this.outlet_id,
        messagingType,
        messagingChannel
      ),
      data: payload,
    });
  }

  /**
   * postMessagingExcelAPI - postMessagingExcelAPI
   */
  public putMessagingExcelAPI(
    payload,
    messagingChannel,
    messagingType: "transactional" | "promotional"
  ): Observable<Response> {
    return this.httpService.putRequest({
      customApiUrl: this.BASE_URL.MESSAGING_BULK_EXCEL(
        this.outlet_id,
        messagingType,
        messagingChannel
      ),
      data: payload,
    });
  }

  /**
   * createTemplateAPI - create template
   */
  public createTemplateAPI(payload: {
    id: string;
    dlt_template_id: string;
    content: string;
    title: string;
    dynamic_variables: {};
  }): Observable<Response> {
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.CREATE_MESSAGE_TEMPLATE_V2(this.outlet_id),
      data: payload,
    });
  }

  /**
   * updateTemplateAPI - update template
   */
  public updateTemplateAPI(
    payload: {
      id: string;
      dlt_template_id: string;
      sms_text?: string;
      content: string;
      title: string;
      dynamic_variables: {};
    },
    template_id: string
  ): Observable<Response> {
    return this.httpService.putRequest({
      customApiUrl: this.BASE_URL.UPDATE_MESSAGE_TEMPLATE_V2(
        this.outlet_id,
        template_id
      ),
      data: payload,
    });
  }
  /**
   * smsBulkAPI - bulk sms
   */
  public transactionalCustomerListAPI(
    payload: {
      dynamic_variables?: {};
      status?: string;
      dlt_template_id?: string;
      sms_text?: string;
      content?: string;
      title?: string;
      template_id?: string;
      customer_list_id?: any;
      message_id?: string | null;
    },
    messagingChannel: "sms" | "whatsapp"
  ): Observable<Response> {
    if (payload?.message_id) {
      return this.httpService.putRequest({
        customApiUrl: this.BASE_URL.MESSAGING_LIST_API(
          this.outlet_id,
          "transactional",
          messagingChannel
        ),
        data: payload,
      });
    }
    return this.httpService.postRequest({
      customApiUrl: this.BASE_URL.MESSAGING_LIST_API(
        this.outlet_id,
        "transactional",
        messagingChannel
      ),
      data: payload,
    });
  }
  /**
   * smsBulkAPI - bulk sms
   */
  public updateDynamicVariables(
    payload: { dynamic_variables: {} },
    templateID: string
  ): Observable<Response> {
    return this.httpService.putRequest({
      customApiUrl: this.BASE_URL.UPDATE_DYNAMIC_VARIABLES(
        this.outlet_id,
        templateID
      ),
      data: payload,
    });
  }

  public downloadCampaignData(messageId: string): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.DOWNLOAD_CAMPAIGN_DATA(
        this.outlet_id,
        messageId
      ),
    });
  }

  /**
   * editPostAPI - Edit post details
   */
  public editPostAPI(postID: string): Observable<Response> {
    return this.httpService.getRequest({
      customApiUrl: this.BASE_URL.VIEW_MESSAGE_V2(this.outlet_id) + postID,
    });
  }

  /**
   * excelSampleFile - Download sample file excel
   */
  public downloadSampleFile(): string {
    return this.BASE_URL.DOWNLOAD_MESSAGING_EXCEL_FILE_URL;
  }

  public closeCalendar(close: boolean) {
    this.closeCalendarModal$.next(close);
  }
}
